import React, { useEffect, useState } from "react";
import { Avatar } from "antd";
import { Message } from "../../layouts/AskDatatram/ChatInterface";
import { FileTextOutlined } from "@ant-design/icons";
import { fetchChatImage } from "src/services/ChatbotAPI";
import "../reusable/Spinner/MiniSpinner.scss";

interface MessageBubbleProps {
  msg: Message;
  WaveIcon: React.ComponentType;
}

const CHAT_IMAGE_PATH_RE = /\/(?:api\/)?(?:chatbot\/)?chat-image\/(\d+)/;

/** Cache decoded image blobs per messageId so remounts don't refetch. */
const chatImageBlobCache = new Map<string, Blob>();
const chatImageInflight = new Map<string, Promise<Blob | null>>();

const extractChatImageMessageId = (src?: string | null): string | null => {
  if (!src || src.startsWith("blob:") || src.startsWith("data:")) {
    return null;
  }
  const match = src.match(CHAT_IMAGE_PATH_RE);
  return match?.[1] ?? null;
};

const getCachedChatImageBlob = (messageId: string): Promise<Blob | null> => {
  const cached = chatImageBlobCache.get(messageId);
  if (cached) {
    return Promise.resolve(cached);
  }

  const existing = chatImageInflight.get(messageId);
  if (existing) {
    return existing;
  }

  const pending = fetchChatImage(messageId)
    .then(blob => {
      if (blob) {
        chatImageBlobCache.set(messageId, blob);
      }
      return blob;
    })
    .finally(() => {
      chatImageInflight.delete(messageId);
    });

  chatImageInflight.set(messageId, pending);
  return pending;
};

export type ChatImagePreviewProps = {
  src?: string | null;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: React.ReactEventHandler<HTMLImageElement>;
};

/**
 * Renders chat images. Authenticated `/chat-image/:id` URLs are fetched with
 * Authorization headers and shown via a revoked-on-unmount blob URL.
 */
export const ChatImagePreview: React.FC<ChatImagePreviewProps> = ({
  src,
  alt,
  className = "chat-image-preview",
  style,
  onError,
}) => {
  const messageId = extractChatImageMessageId(src);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(messageId));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!messageId) {
      setBlobUrl(null);
      setLoading(false);
      setFailed(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setLoading(true);
    setFailed(false);
    setBlobUrl(null);

    getCachedChatImageBlob(messageId).then(blob => {
      if (cancelled) {
        return;
      }
      if (!blob) {
        setFailed(true);
        setLoading(false);
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      if (cancelled) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
        return;
      }
      setBlobUrl(objectUrl);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [messageId]);

  if (messageId) {
    if (failed) {
      return null;
    }
    if (loading || !blobUrl) {
      return (
        <div
          className={className}
          style={style}
          aria-busy="true"
          aria-label={alt || "Loading image"}
        />
      );
    }
    return (
      <img
        src={blobUrl}
        alt={alt}
        className={className}
        style={style}
        onError={event => {
          setFailed(true);
          onError?.(event);
        }}
      />
    );
  }

  if (!src) {
    return null;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      onError={onError}
    />
  );
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({ msg, WaveIcon }) => {
  if (!msg.text) {return null;}

  return (
    <div
      className={`message-row ${msg.sender} ${
        msg.type === "botQuestion" ? "bot-question" : ""
      }`}
    >
      {msg.sender === "bot" && (
        <div className="chatbot-avatar">
          <Avatar size={35} icon={<WaveIcon />} alt="Chatbot Avatar" />
        </div>
      )}

      <div
        className={`message-bubble ${
          msg.sender === "bot" ? "bot-bubble" : "user-bubble"
        }`}
      >
        {msg.text}
      </div>
    </div>
  );
};

export const FileMessage: React.FC<{ msg: Message }> = ({ msg }) => {
  if (!msg.file) {return null;}

  return (
    <div className="chat-file">
      {msg.file?.type?.startsWith("image/") ? (
        <ChatImagePreview
          src={msg.file.url}
          alt={msg.file.name}
          className="chat-image-preview"
        />
      ) : (
        <a
          href={msg.file.url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="chat-file-link"
        >
          <FileTextOutlined className="message-icon" />
           {msg.file.name}
        </a>
      )}
    </div>
  );
};
